import { Injectable } from '@angular/core';
import { AlertButtonRole, AlertInputOptions } from 'providers/alert-controller';
import { AlertController } from 'providers/alert-controller';
import { ToastController } from 'providers/toast-controller';
import { CloudFunctionsService } from './cloud-functions.service';
import { TelemetryService } from './telemetry.service';
import { AuthService } from './auth.service';
import { LoggingService } from './logging.service';
import { errorReportFields } from './error-patterns';
import { FlagContentParams, FlagReason } from '@cf-shared/flag-content-interface';
import { FoodHeader } from '@cf-shared/data-model/food';
import { LL } from 'i18n/i18n.service';

/**
 * How long the "thanks, we got your report" confirmation stays up. A toast with no duration at
 * all never leaves by itself: the only way out is the small close mark in its corner, so a
 * missed tap leaves it sitting over the food list indefinitely. Every toast this service
 * raises was missing a duration, which is what these two constants fix.
 */
const FLAG_SUCCESS_TOAST_DURATION_MS = 3000;

/** The failure message gets longer than the confirmation — it asks the user to try again. */
const FLAG_ERROR_TOAST_DURATION_MS = 5000;

/**
 * The name a failed food report is filed under in Sentry, and the `log_message` it travels with
 * on the `sentry_error` Mixpanel event. One name covers both ways the report can fail — the
 * server refusing it and the call never getting there — because the `failure` field in the
 * context tells those two apart, and two separate Sentry issues for one broken thing would
 * only split the count of it.
 */
const FLAG_REPORT_FAILED_PROBLEM = 'FlagContentReportFailed';

@Injectable()
export class FlagContentService {

  constructor(
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private cloudFunctions: CloudFunctionsService,
    private telemetry: TelemetryService,
    private auth: AuthService,
    private logging: LoggingService,
  ) {}

  async showFlagDialogAsync(food: FoodHeader): Promise<void> {
    const inputs: AlertInputOptions[] = [
      { type: 'radio', label: LL.flagContent.reasons.offensive(), description: LL.flagContent.reasons.offensiveDesc(), value: FlagReason.Offensive },
      { type: 'radio', label: LL.flagContent.reasons.spam(), description: LL.flagContent.reasons.spamDesc(), value: FlagReason.Spam },
      { type: 'radio', label: LL.flagContent.reasons.incorrect(), description: LL.flagContent.reasons.incorrectDesc(), value: FlagReason.Incorrect },
      { type: 'radio', label: LL.flagContent.reasons.other(), description: LL.flagContent.reasons.otherDesc(), value: FlagReason.Other },
    ];

    const reason = await this.alertCtrl.showAlertAsync<FlagReason>({
      title: LL.flagContent.title(),
      inputs,
      buttons: [
        { text: LL.flagContent.submit(), role: AlertButtonRole.Primary, result: (data: any) => data },
      ],
      implicitCancelResult: null,
    });

    if (!reason) return;

    let description: string | undefined;

    // if "Other" selected, show follow-up text input for description
    if (reason === FlagReason.Other) {
      const result = await this.alertCtrl.showAlertAsync<{ description: string }>({
        title: LL.flagContent.otherTitle(),
        inputs: [
          { type: 'text', name: 'description', placeholder: LL.flagContent.otherPlaceholder() },
        ],
        buttons: [
          { text: LL.flagContent.submit(), role: AlertButtonRole.Primary, result: (data: any) => data },
        ],
        implicitCancelResult: null,
      });

      if (!result) return;
      description = result.description;
    }

    const params: FlagContentParams = {
      type: 'flagContent',
      contentType: 'food',
      contentId: food._id,
      contentName: food.name,
      reason,
      description,
      reporterName: this.auth.user.name,
      replyToEmail: this.auth.user.email,
      commonEventParams: this.telemetry.getCommonEventParams(),
    };

    // The `content_flagged` event is recorded in the success branch ONLY, and the two failure
    // branches report the failure instead. It used to sit after this whole block with nothing
    // guarding it, so it was recorded in all three outcomes — a report the server refused, and
    // one that never reached the server at all, both counted as a report we received. Anyone
    // reading the event count was told more food reports had arrived than really had, and
    // because neither failure branch reported anything, the size of that gap was invisible.
    try {
      const result = await this.cloudFunctions.sendFeedbackAsync(params);
      if (result.error) {
        this.logging.trackSilentProblem(FLAG_REPORT_FAILED_PROBLEM, { failure: 'server-refused', reason, serverError: result.error });
        this.toastCtrl.showToast({ message: LL.flagContent.error(), duration: FLAG_ERROR_TOAST_DURATION_MS });
      } else {
        this.telemetry.trackEvent('content_flagged', { contentType: 'food', reason });
        this.toastCtrl.showToast({ message: LL.flagContent.success(), duration: FLAG_SUCCESS_TOAST_DURATION_MS });
      }
    } catch (err) {
      this.logging.trackSilentProblem(FLAG_REPORT_FAILED_PROBLEM, { failure: 'call-threw', reason, ...errorReportFields(err) });
      this.toastCtrl.showToast({ message: LL.flagContent.error(), duration: FLAG_ERROR_TOAST_DURATION_MS });
    }
  }
}
